import type { ReactNode } from "react";
import { Link, NavLink } from "react-router";
import { useAuth } from "../app/AuthProvider";
import { landingFor, PUBLIC_NAV } from "../app/navigation";
import { cx, LinkButton } from "../ui/primitives";
import { BackendModeBadge } from "./BackendModeBadge";

export function PublicShell({ children }: { children: ReactNode }) {
	const { session, role } = useAuth();

	return (
		<div className="flex min-h-full flex-col bg-white">
			<header className="sticky top-0 z-20 border-b border-ink-200 bg-white/95 backdrop-blur">
				<div className="mx-auto flex max-w-6xl items-center gap-6 px-4 py-3 lg:px-8">
					<Link to="/" className="text-[19px] font-bold tracking-tight text-ink-900">
						Agentry
					</Link>
					<nav className="hidden gap-1 md:flex" aria-label="サービス案内">
						{PUBLIC_NAV.map((item) => (
							<NavLink
								key={item.to}
								to={item.to}
								className={({ isActive }) =>
									cx(
										"rounded-lg px-3 py-1.5 text-[14px] transition-colors",
										isActive
											? "bg-ink-100 font-semibold text-ink-900"
											: "text-ink-600 hover:text-ink-900",
									)
								}
							>
								{item.label}
							</NavLink>
						))}
					</nav>
					<div className="ml-auto flex items-center gap-2">
						<BackendModeBadge />
						{session ? (
							<LinkButton to={landingFor(role)} size="sm">
								{session.user.name} さんの画面へ
							</LinkButton>
						) : (
							<>
								<LinkButton to="/login" variant="ghost" size="sm">
									ログイン
								</LinkButton>
								<LinkButton to="/signup" size="sm">
									無料で始める
								</LinkButton>
							</>
						)}
					</div>
				</div>
			</header>

			<main className="flex-1">{children}</main>

			<footer className="border-t border-ink-200 bg-ink-50">
				<div className="mx-auto grid max-w-6xl gap-8 px-4 py-10 sm:grid-cols-2 lg:grid-cols-4 lg:px-8">
					<div>
						<p className="text-[17px] font-bold text-ink-900">Agentry</p>
						<p className="mt-2 max-w-xs text-[13px] text-ink-600">
							専門Agentが質問し、調べ、作り、品質を確認します。あなたは決めるだけ。
						</p>
					</div>
					<FooterColumn
						title="サービス"
						links={[
							{ label: "Agent一覧", to: "/agents" },
							{ label: "業種から探す", to: "/solutions" },
							{ label: "料金", to: "/pricing" },
							{ label: "デモを試す", to: "/demo?api=mock" },
						]}
					/>
					<FooterColumn
						title="はじめる"
						links={[
							{ label: "新規登録", to: "/signup" },
							{ label: "ログイン", to: "/login" },
							{ label: "パスワード再設定", to: "/password-reset" },
							{ label: "お問い合わせ", to: "/contact" },
						]}
					/>
					<FooterColumn
						title="規約"
						links={[
							{ label: "利用規約", to: "/legal/terms" },
							{ label: "プライバシーポリシー", to: "/legal/privacy" },
							{ label: "特定商取引法に基づく表記", to: "/legal/tokushoho" },
							{ label: "セキュリティ", to: "/legal/security" },
						]}
					/>
				</div>
				<div className="border-t border-ink-200 px-4 py-4 text-center text-[12px] text-ink-500">
					© 2026 Agentry. All rights reserved. · WO-E2E-1
				</div>
			</footer>
		</div>
	);
}

function FooterColumn({
	title,
	links,
}: {
	title: string;
	links: Array<{ label: string; to: string }>;
}) {
	return (
		<div>
			<p className="text-[13px] font-bold text-ink-800">{title}</p>
			<ul className="mt-2 space-y-1.5">
				{links.map((link) => (
					<li key={link.to}>
						<Link to={link.to} className="text-[13px] text-ink-600 hover:text-brand-600">
							{link.label}
						</Link>
					</li>
				))}
			</ul>
		</div>
	);
}
